/**
 * Content Index — Optimized lookup structures for matching TVOD products to VOD movies
 * and filtering by genre.
 *
 * Data structures:
 *   - vodByUid: Map<uid, movie> — O(1) movie lookup by UID
 *   - genreIndex: { genreId: Set<uid> } — O(1) genre filtering
 *   - enrichedProducts: TVOD products merged with matched movie data + resolved genres
 *
 * Usage:
 *   const index = buildContentIndex(vodMovies, tvodProducts, genreMap);
 *   const actionProducts = index.filterByGenre(genreId);
 *   const movie = index.getMovieByUid("rajiandthebeast");
 *   const allEnriched = index.enrichedProducts;
 */

/**
 * Build all lookup structures in a single pass.
 *
 * @param {Array} vodMovies — VOD movies from fetchAllContent (each has uid, movie_genres, image_id, title, etc.)
 * @param {Array} tvodProducts — TVOD products from user TV store (each has uid, name, card_image_id, price_options, etc.)
 * @param {Object} genreMap — { genreId: genreName } from fetchGenres()
 * @returns {Object} index with lookup methods
 */
export const buildContentIndex = (vodMovies = [], tvodProducts = [], genreMap = {}) => {
  console.log("[ContentIndex] Building index:", vodMovies.length, "VOD movies,", tvodProducts.length, "TVOD products,", Object.keys(genreMap).length, "genres");

  // 1. Map<uid, movie> — O(1) lookup by UID
  const vodByUid = new Map();
  vodMovies.forEach((m) => {
    vodByUid.set(m.uid, m);
  });

  // 2. Inverted genre index: { genreId: Set<uid> } — O(1) genre filtering
  const genreIndex = {};
  vodMovies.forEach((m) => {
    const genres = m.movie_genres || m.genres || [];
    genres.forEach((gid) => {
      const id = gid.id || gid; // handle both {id:123} and plain 123
      if (!genreIndex[id]) genreIndex[id] = new Set();
      genreIndex[id].add(m.uid);
    });
  });

  // 3. Fuzzy UID matching — handle minor slug inconsistencies
  //    (e.g. "bonnyclara" vs "bonnyandclara", "sammiegina" vs "sammieandgina")
  //    Strategy: strip common conjunctions/articles that slug generators handle inconsistently,
  //    then remove all non-alphanumeric. Only strip words that appear as SEPARATORS between
  //    name parts (bounded by word-start/end), not within names.
  const STRIP_WORDS = /\b(and|the|of|in|at|to)\b/gi;
  const normalizeUid = (uid) => {
    if (!uid) return "";
    return uid.toLowerCase().replace(STRIP_WORDS, "").replace(/[^a-z0-9]/g, "");
  };
  const vodByNormalizedUid = new Map();
  vodMovies.forEach((m) => {
    const key = normalizeUid(m.uid);
    // Only add if no exact-match collision — exact always wins
    if (!vodByUid.has(key)) {
      vodByNormalizedUid.set(key, m);
    }
  });

  // 4. Enrich TVOD products with matched VOD movie data
  const enrichedProducts = tvodProducts.map((prod) => {
    // Try exact match first, then fuzzy
    let movie = vodByUid.get(prod.uid) || vodByNormalizedUid.get(normalizeUid(prod.uid)) || null;

    const movieGenres = movie
      ? (movie.movie_genres || movie.genres || []).map((gid) => {
          const id = gid.id || gid;
          return genreMap[id] || null;
        }).filter(Boolean)
      : [];

    return {
      ...prod,
      movie,
      movieId: movie?.id || null,
      movieUid: movie?.uid || null,
      movieTitle: movie?.title || prod.name,
      movieImageId: movie?.image_id || prod.card_image_id,
      movieType: movie?.type || "movie",
      genres: movieGenres,
      genreIds: movie ? (movie.movie_genres || movie.genres || []).map((g) => g.id || g) : [],
    };
  });

  // Log match results
  const matched = enrichedProducts.filter((p) => p.movie);
  const unmatched = enrichedProducts.filter((p) => !p.movie);
  console.log("[ContentIndex] Matched:", matched.length, "| Unmatched:", unmatched.length,
    unmatched.length > 0 ? "(" + unmatched.map((p) => p.uid).join(", ") + ")" : "");

  // Log genre index stats
  const genreStats = Object.entries(genreIndex).map(([gid, uids]) => `${genreMap[gid] || gid}:${uids.size}`);
  console.log("[ContentIndex] Genre index:", genreStats.join(", "));

  // 5. Return the index object with lookup methods
  return {
    /** Map of uid → movie object */
    vodByUid,

    /** Inverted genre index: genreId → Set<uid> */
    genreIndex,

    /** TVOD products enriched with matched movie data + genres */
    enrichedProducts,

    /** Get VOD movie by exact or fuzzy UID match — O(1) */
    getMovieByUid: (uid) => vodByUid.get(uid) || vodByNormalizedUid.get(normalizeUid(uid)) || null,

    /** Filter enriched products by genre ID — O(k) where k = results */
    filterByGenre: (genreId) => {
      const matchingUids = genreIndex[genreId];
      if (!matchingUids) return [];
      return enrichedProducts.filter((p) => matchingUids.has(p.uid) || matchingUids.has(p.movieUid));
    },

    /** Filter enriched products by genre name — O(k) */
    filterByGenreName: (genreName) => {
      // Find genre ID from name
      const entry = Object.entries(genreMap).find(
        ([, name]) => name.toLowerCase() === genreName.toLowerCase()
      );
      if (!entry) return [];
      return enrichedProducts.filter((p) => {
        const matchingUids = genreIndex[entry[0]];
        return matchingUids && (matchingUids.has(p.uid) || matchingUids.has(p.movieUid));
      });
    },

    /** Get all unique genre names that have TVOD products */
    getAvailableGenres: () => {
      const available = new Map();
      enrichedProducts.forEach((p) => {
        p.genreIds.forEach((gid) => {
          if (genreMap[gid] && !available.has(gid)) {
            available.set(gid, genreMap[gid]);
          }
        });
      });
      return Array.from(available.entries()).map(([id, name]) => ({ id: Number(id), name }));
    },

    /** Stats for debugging */
    stats: {
      vodCount: vodMovies.length,
      tvodCount: tvodProducts.length,
      matchedCount: matched.length,
      unmatchedCount: unmatched.length,
      genreCount: Object.keys(genreIndex).length,
    },
  };
};
